import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  selectAstSignMailHandoffStatus,
  selectIsAstSignMailDestinationClaimed,
  selectIsAstSignMailQueueReturnNoticeVisible,
  selectTextOnSignMail,
} from './astSignMailSelectors';
import {
  claimAstSignMailHandoff,
  dismissAstSignMailQueueReturnNotice,
  endAstSignMailHandoff,
  showAstSignMailQueueReturnNotice,
  resetAstSignMail,
  startAstSignMailHandoff,
  transferAstRelayResponseReceived,
} from './astSignMailSlice';
import { removeCurrentAgentFromVoiceSession } from './astSignMailHandoffHelpers';

import type { RootState } from 'features/app/store/store';
import { sendAnalyticsInfo } from 'features/analytics/helpers';
import {
  transferCallOwnershipToAST,
  transferCallToAST,
} from 'features/ast-signmail/helpers';
import { AST_SIGNMAIL_TEXT_MAX_LENGTH } from 'features/ast-signmail/constants';
import {
  AstSignMailHandoffStatus,
  type AstSignMailTransferRequest,
} from 'features/ast-signmail/interfaces';
import { isPositiveUserId } from 'features/call/call-base/helpers';
import type {
  Call,
  TransferAstRelayResponse,
} from 'features/call/call-base/interfaces';
import { CallDaoService } from 'features/call/call-base/services';
import { flushOpenCaptionStatuses } from 'features/call/call-captions';
import {
  callIdSelector,
  coreRingGroupOwnerUserIdSelector,
  coreServicesUserIdSelector,
  finalizeLocalCallExit,
  callIdMatchesCurrentCall,
} from 'features/call/call-base/store';
import { setDeafStatus } from 'features/call/call-deaf/store';
import { primaryHearingSelector } from 'features/call/call-hearing/store';
import { handleError } from 'features/notification/store';
import { VoiceMeetingEventBus } from 'features/voice-meeting/services';
import {
  voiceSessionHandedOff,
  voiceSessionIdSelector,
} from 'features/voice-session/store';
import { agentNumberSelector } from 'features/user/store';

class StaleAstSignMailCallError extends Error {}

const buildAstOwnershipCallUpdate = (
  coreServicesUserId: number | null,
  coreRingGroupOwnerUserId: number | null
): Partial<Call> => {
  const update: Partial<Call> = {};

  if (isPositiveUserId(coreServicesUserId)) {
    update.CoreServicesUserId = coreServicesUserId;
  }

  // Persist the hearing-lookup ring-group owner on the Relay call before the
  // AST transfer. Written whenever positive; no flag gates it.
  if (isPositiveUserId(coreRingGroupOwnerUserId)) {
    update.CoreRingGroupOwnerUserId = coreRingGroupOwnerUserId;
  }

  return update;
};

export const finalizeAstSignMailLocalExit = createAsyncThunk(
  'astSignMail/finalizeAstSignMailLocalExit',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    if (
      selectAstSignMailHandoffStatus(state) !==
      AstSignMailHandoffStatus.TRANSFERRING
    ) {
      return;
    }

    const voiceSessionId = voiceSessionIdSelector(state);
    const agentNumber = agentNumberSelector(state);

    dispatch(startAstSignMailHandoff());
    dispatch(showAstSignMailQueueReturnNotice());
    VoiceMeetingEventBus.detachLocalSession();

    const removalStatus = await removeCurrentAgentFromVoiceSession({
      voiceSessionId,
      agentNumber,
      dispatch,
    });

    dispatch(
      sendAnalyticsInfo({
        Method: 'finalizeAstSignMailLocalExit',
        message: `AST SignMail voice exit initiated with agent participant ${removalStatus}`,
        LogGroup: 'AstSignMail',
      })
    );

    try {
      await dispatch(finalizeLocalCallExit()).unwrap();
      dispatch(
        sendAnalyticsInfo({
          Method: 'finalizeAstSignMailLocalExit',
          message:
            'AST SignMail hearing call disconnected and local call reset; queue return notice still showing',
          LogGroup: 'AstSignMail',
        })
      );
    } catch (error) {
      dispatch(dismissAstSignMailQueueReturnNotice());
      dispatch(endAstSignMailHandoff());
      dispatch(
        handleError({
          error,
          methodName: 'finalizeAstSignMailLocalExit',
        })
      );
    }
  }
);

export const attemptAutoAstSignMailHandoff = createAsyncThunk(
  'astSignMail/attemptAutoAstSignMailHandoff',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);

    if (!callIdMatchesCurrentCall(state, callId)) {
      dispatch(
        sendAnalyticsInfo({
          Method: 'attemptAutoAstSignMailHandoff',
          message:
            'AST SignMail handoff canceled because this call is no longer active.',
          capturedCallId: callId,
          currentCallId: callIdSelector(getState() as RootState),
          LogGroup: 'AstSignMail',
        })
      );
      return;
    }

    if (selectIsAstSignMailDestinationClaimed(state)) {
      dispatch(
        sendAnalyticsInfo({
          Method: 'attemptAutoAstSignMailHandoff',
          message:
            'AST SignMail handoff skipped because destination is already claimed.',
          LogGroup: 'SignMail',
        })
      );
      return;
    }

    // Async transfer steps may resolve after this call has reset or been
    // replaced. Throwing a sentinel keeps every stale completion fail-silent.
    const assertCurrentCall = () => {
      if (!callIdMatchesCurrentCall(getState() as RootState, callId)) {
        throw new StaleAstSignMailCallError();
      }
    };

    // Claim before ownership await so local SignMail cannot start mid-transfer.
    dispatch(claimAstSignMailHandoff());

    try {
      try {
        const callUpdate = buildAstOwnershipCallUpdate(
          coreServicesUserIdSelector(state),
          coreRingGroupOwnerUserIdSelector(state)
        );

        if (Object.keys(callUpdate).length > 0) {
          await CallDaoService.updateCall(callId, callUpdate);
        }
      } catch (error) {
        assertCurrentCall();

        dispatch(endAstSignMailHandoff());
        dispatch(
          handleError({
            error,
            methodName: 'updateCallCoreServicesUserId',
            disableUserNotification: true,
          })
        );
        throw error;
      }

      assertCurrentCall();

      let transferAstRelayResponse: TransferAstRelayResponse;

      // Relay ownership must move to AST before any local handoff UI or media
      // cleanup begins. Offs this client sent must land first; AST will not
      // close them.
      try {
        await flushOpenCaptionStatuses({ dispatch });
        transferAstRelayResponse = await transferCallOwnershipToAST(callId);
      } catch (error) {
        assertCurrentCall();

        dispatch(endAstSignMailHandoff());
        dispatch(
          handleError({
            error,
            methodName: 'transferCallOwnershipToAST',
            disableUserNotification: true,
          })
        );
        throw error;
      }

      assertCurrentCall();

      // Record successful AST ownership before applying its local call effects.
      dispatch(transferAstRelayResponseReceived(transferAstRelayResponse));
      // The AST agent joins this voice session, and the Switchboard transfer
      // below disconnects the hearing, so this client must not delete it.
      dispatch(
        voiceSessionHandedOff({
          sessionId: voiceSessionIdSelector(getState() as RootState),
        })
      );
      dispatch(setDeafStatus('unavailable'));

      assertCurrentCall();

      dispatch(
        sendAnalyticsInfo({
          Method: 'startAutoSignMailFlow',
          message: 'AST SignMail handoff started for auto-triggered SignMail.',
          LogGroup: 'SignMail',
        })
      );

      const transferState = getState() as RootState;
      assertCurrentCall();
      const callobject: AstSignMailTransferRequest = {
        ...transferAstRelayResponse,
        TextOnSignMail: Array.from(selectTextOnSignMail(transferState))
          .slice(0, AST_SIGNMAIL_TEXT_MAX_LENGTH)
          .join(''),
      };

      // Ownership has already moved, so a Switchboard transfer failure is
      // reported but must not fall back to local SignMail. The ids are read
      // inside the try because the hearings list can be empty by now.
      try {
        const sessionId = voiceSessionIdSelector(transferState);
        const hearingParticipantId = primaryHearingSelector(transferState)?.id;

        if (!sessionId || !hearingParticipantId) {
          throw new Error(
            'AST SignMail transfer requires a voice session id and a primary hearing participant id'
          );
        }

        await transferCallToAST({
          sessionId,
          hearingParticipantId,
          callobject,
        });
      } catch (error) {
        assertCurrentCall();

        dispatch(
          handleError({
            error,
            methodName: 'transferCallToAST',
            disableUserNotification: true,
          })
        );
      }

      assertCurrentCall();

      await dispatch(finalizeAstSignMailLocalExit()).unwrap();
    } catch (error) {
      if (error instanceof StaleAstSignMailCallError) {
        dispatch(resetAstSignMail());
        dispatch(
          sendAnalyticsInfo({
            Method: 'attemptAutoAstSignMailHandoff',
            message:
              'AST SignMail handoff canceled because this call is no longer active.',
            capturedCallId: callId,
            currentCallId: callIdSelector(getState() as RootState),
            LogGroup: 'AstSignMail',
          })
        );
        return;
      }

      dispatch(dismissAstSignMailQueueReturnNotice());
      dispatch(endAstSignMailHandoff());
      throw error;
    }
  }
);

export const completeAstSignMailHandoff = createAsyncThunk(
  'astSignMail/completeAstSignMailHandoff',
  (_, { dispatch, getState }) => {
    if (!selectIsAstSignMailQueueReturnNoticeVisible(getState() as RootState)) {
      return;
    }

    dispatch(dismissAstSignMailQueueReturnNotice());
    dispatch(
      sendAnalyticsInfo({
        Method: 'completeAstSignMailHandoff',
        message: 'AST SignMail queue return notice dismissed',
        LogGroup: 'AstSignMail',
      })
    );
  }
);
